<template lang="pug">
.workflow-fields__field(v-if="conditionShow")
  .form-field-label
    span {{ field.label }}
    span.form-field-require-tag(v-show="field.require", :title="notifies.required_field")
      | *
    q-icon.text-h6.workflow-fields__icon-reset.cursor-pointer(
      name="refresh",
      @click="resetSelectField",
      v-if="mayBeReset"
    )
  .form-field.form-multiselect
    Multiselect(
      :value="workflowSelectField",
      :track-by="'id'",
      :label="'title'",
      :options="workflowSelectFieldOptions",
      placeholder="",
      :multiple="field.multiple",
      :hideSelected="true",
      :show-labels="false",
      open-direction="bottom",
      :allow-empty="true",
      @select="setField",
      @remove="removeField",
      @search-change="onSearchChange",
      :close-on-select="!field.multiple"
    )
      span(slot="noResult")
        .spinner-container.select-field-spinner(v-if="loading")
          q-spinner(color="primary", size="1.7em")
        i(v-else) {{ notifies.no_search_result }}

      span(slot="noOptions")
        i {{ notifies.no_options_list }}

      template(slot="afterList")
        div(v-if="areOptionsAvailable", style="text-align: center")
          div(v-observe-visibility="reachedEndOfList")
          span(style="padding: 10px") ...

    span.valid-error-message(v-if="fieldInvalidMessage") {{ fieldInvalidMessage }}
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import _ from "lodash";

import Multiselect from "vue-multiselect";
import backend from "@/api";
import { workflowConstructorLocales } from "@/services/useLocales";
import { handleError } from "@/services/handleErrors";

const props = defineProps({
  /* объект, содержащий в себе поле, которое мы отображаем, его параметры и прочие моменты (наше дефолтное поле) 
  { 
    "label":"Событие",
    "name":"trigger_type",
    "type":"select",
    "options":[{"id":"max_duration_expired","title":"По истечении времени"}],
    "field_id":"trigger_type",
    "value":null,
    "require":true
  }
  */
  field: { type: Object, default: () => {} },
  /* объект с данными о текщей редактируемой сущности (все поля формы), используется для получения первоначальных данных по имени поля и других манипуляций, можешь варьироваться */
  object: { type: Object, default: () => {} },
  /* объект, содержащий в себе статус в формате признак: значение 
  {
    "id":1,
    "title":"новая",
    "workflow_id":1,
    "started":true,
    "terminated":false,
    "pause_available":false,
    "accepted":false,
    "percentage_completion":0,
    "template":{"color":"#5b7aff","position":["357","70"]},
    "created_at":"2023-04-25 09:16:43 UTC",
    "updated_at":"2025-05-20 13:46:03 UTC",
    "deleted_at":null,
    "roles":["2","3","4","9","10","11","12","13"],
    "external_id":null,
    "max_duration":0,
    "formated_duration":"0с.",
    "activities":[],
    "transitions":
      [
        {
          "id":1,
          "title":"Принять",
          "old_status":{"id":1,"title":"новая"},
          "new_status":{"id":2,"title":"принята"},
          "form":{
            "id":1,
            "title":"Accept Form",
            "validations":[
              {
                "id":7075,
                "attribute":"executor_id",
                "type":null,
                "options":{},
                "attribute_title":"Исполнитель"
              },
            ]
          },
          "roles":["2","4","10","3","5","9","11","12","13","16"],
          "template":{"color":"#c181ff"},
          "created_at":"2023-04-25 09:16:43 UTC",
          "updated_at":"2025-02-27 11:27:04 UTC"},
        }
      ],
      "days":0,
      "hours":0,
      "minutes":0
    }
  */
  status: { type: Object, default: null },
  /* булеан зависимости поля от некоего признака, настраиваемого в главном компоненте формы (полю достаточно только установить его в true/false, остальное отслеживает форма) */
  conditionShow: { type: Boolean, default: true },
  /* идентификатор текущего воркфлоу */
  workflowId: { type: Number, default: null },
});

const emit = defineEmits(["fieldChange"]);

const loading = ref(false);
const page = ref(1);
const perPage = 20;
const searchQuery = ref(null);
const searchTimeout = ref(null);
const workflowSelectField = ref(null);
const workflowSelectFieldOptions = ref([]);
const workflowSelectFieldOptionsCount = ref();
const defaultValues = ref([]);

const hasNextPage = () => {
  // Extra check for case if backend would send null or undefined
  if (workflowSelectFieldOptions.value?.length === 0) {
    return false;
  }

  const lastPage = Math.ceil(workflowSelectFieldOptionsCount.value / perPage);
  const currentPage = page.value;

  return currentPage <= lastPage;
};

const reachedEndOfList = async reachedEnd => {
  if (reachedEnd && !loading.value) {
    await getSelectOptions();
  }
};

const areOptionsAvailable = computed(
  () => workflowSelectFieldOptions.value && workflowSelectFieldOptionsCount.value && hasNextPage(),
);

const fieldInvalidMessage = computed(() => {
  if (props.field?.name === "trigger_type") {
    if (!props.status.max_duration) {
      return workflowConstructorLocales.value.max_duration_not_set;
    }
  }
  return undefined;
});

const setDefaultValues = () => {
  if (props.field.multiple && props.object[props.field.name]) {
    defaultValues.value = props.object[props.field.name].map(el => el?.id);
  } else {
    defaultValues.value.push(props.object[props.field.name]?.id);
  }
};

const setField = val => {
  searchQuery.value = null;

  if (props.field.multiple) {
    if (workflowSelectField.value?.length) {
      workflowSelectField.value.push(val);
    } else {
      workflowSelectField.value = [val];
    }
  } else {
    workflowSelectField.value = val;
  }

  emitFieldChange();
};

const removeField = val => {
  const answerIndex = workflowSelectField.value.findIndex(el => el.id === val.id);

  if (answerIndex !== -1) {
    workflowSelectField.value.splice(answerIndex, 1);
  }

  defaultValues.value = defaultValues.value.filter(item => item !== val.id);
  emitFieldChange();
};

const resetSelectField = () => {
  searchQuery.value = null;
  workflowSelectField.value = null;
  emitFieldChange();
};

const emitFieldChange = () => {
  emit("fieldChange", {
    name: props.field.name,
    value: getCurrentValue.value,
  });

  if (props.field.multiple) {
    if ((!workflowSelectField.value?.length && props.field.require) || fieldInvalidMessage.value) {
      emit("disableField", props.field.name);
    } else {
      emit("enableField", props.field.name);
    }
  } else {
    if ((!workflowSelectField.value && props.field.require) || fieldInvalidMessage.value) {
      emit("disableField", props.field.name);
    } else {
      emit("enableField", props.field.name);
    }
  }
};

const generateCollectionLink = computed(() => {
  if (props.field?.options_path === "statuses") {
    return `api/v3/workflows/${props.workflowId}/statuses/collection`;
  } else {
    return `api/v3/${props.field.options_path}/collection`;
  }
});

const mayBeReset = computed(() => {
  if (props.field.multiple) {
    return workflowSelectField.value?.length > 0;
  } else {
    return !!workflowSelectField.value;
  }
});

const generateCollectionParams = () => {
  const params = props.field.options_params;
  params.infinite_scroll = { page: page.value, per_page: perPage };

  if (props.field?.name === "action_value" && props.field?.options_path === "statuses") {
    params.status_id = props.status.id;
  } else if (props.field?.name === "action_value" && props.field?.options_path !== "statuses") {
    params.workflow_id = props.workflowId;
  }

  params["default_value"] = defaultValues.value;
  params["search_query"] = searchQuery.value ?? "";

  return params;
};

const getSelectOptions = async () => {
  try {
    loading.value = true;

    if (props.field.options) {
      workflowSelectFieldOptions.value = props.field.options;
    } else if (!props.field.options) {
      const data = await backend.collection(generateCollectionLink.value, generateCollectionParams());
      const result = data.data;

      workflowSelectFieldOptions.value = _.unionBy(workflowSelectFieldOptions.value, result.options, "id");
      workflowSelectFieldOptionsCount.value = result.count;

      page.value += 1;
    } else {
      throw new Error("Unexpected Error in Select Field while loading options.");
    }
  } catch (e) {
    await handleError(e);
  } finally {
    loading.value = false;
  }
};

// Функция сброса таймаута для дебаунса поиска
const clearSearchTimeout = () => {
  if (searchTimeout.value) clearTimeout(searchTimeout.value);
};

const onSearchChange = query => {
  clearSearchTimeout();

  searchTimeout.value = setTimeout(async () => {
    workflowSelectFieldOptions.value = [];
    workflowSelectFieldOptionsCount.value = undefined;
    page.value = 1;

    if (query) {
      searchQuery.value = query.length > 0 ? query : null;
    } else {
      searchQuery.value = null;
    }

    await getSelectOptions();
  }, 500);
};

const getCurrentValue = computed(() => {
  if (props.field.multiple) {
    if (workflowSelectField.value?.length) {
      return workflowSelectField.value.map(el => {
        return el?.id;
      });
    } else {
      return [];
    }
  } else {
    return workflowSelectField.value?.id;
  }
});

const getInitialFieldValue = () => {
  if (props.object[props.field.name]) {
    if (props.field.multiple) {
      workflowSelectField.value = props.object[props.field.name]?.length ? generateArrayOfId() : null;
    } else {
      workflowSelectField.value = [findWorkflowOption(props.object[props.field.name])];
    }
  }
};

const generateArrayOfId = () => {
  return props.object[props.field.name].map(el => findWorkflowOption(el));
};

const findWorkflowOption = element => {
  if (element?.id) {
    return workflowSelectFieldOptions.value.find(option => {
      return option.id === element.id;
    });
  } else {
    return workflowSelectFieldOptions.value.find(option => {
      return option.id === element;
    });
  }
};

onMounted(async () => {
  setDefaultValues();
  await getSelectOptions();
  getInitialFieldValue();
});

onBeforeUnmount(() => {
  clearSearchTimeout();
  if (searchTimeout.value) clearTimeout(searchTimeout.value);
});
</script>
